import { Icon, Input } from '@billy/design-system'
import { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'

import { useInvoiceComplianceMode } from '@features/invoices/hooks/useInvoiceComplianceMode'
import { countryIdToRegistrationNoPlaceholderTKey } from '@features/invoicingSettings/constants/countryIdToRegistrationNoPlaceholderTKey'
import { SiretPendingAssignmentCheckbox } from '@features/invoicingSettings/elements/SiretPendingAssignment'
import { useShouldShowSiretPendingAssignmentCheckbox } from '@features/invoicingSettings/hooks/useShouldShowSiretPendingAssignmentCheckbox'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { useResolveTaxAndRegistrationNoLabel } from '../../../../../../../../../../../../hooks/useResolveTaxAndRegistrationNoLabel'
import { useMissingCompanyDetailsForm } from '../../../../../../contexts/missingCompanyDetailsFormContext'

export const CompanyRegistrationNumber = (): ReactElement | null => {
  const { t } = useTranslation()
  const {
    missingCompanyDetailsForm: {
      FormItem,
      formState: { errors },
      trigger,
      watch,
    },
  } = useMissingCompanyDetailsForm()
  const { organization } = useUserOrganization()
  const { registrationNoLabel } = useResolveTaxAndRegistrationNoLabel()
  const { isFrenchComplianceMode } = useInvoiceComplianceMode()
  const shouldShowSiretPendingCheckbox = useShouldShowSiretPendingAssignmentCheckbox()

  const [isSiretPendingAssignmentValue, registrationNoValue] = watch(['isSiretPendingAssignment', 'registrationNo'])

  const countryId = organization?.countryId
  const defaultRegistrationNoPlaceholder = countryId ? t(countryIdToRegistrationNoPlaceholderTKey[countryId]) : ''
  const vatNoError = errors?.vatNo
  const registrationNoPlaceholder = isSiretPendingAssignmentValue
    ? t('external_invoices.missing_customer_compliance_details.modal.form.field.siret_number.placeholder')
    : defaultRegistrationNoPlaceholder

  return (
    <>
      <FormItem
        label={registrationNoLabel}
        name="registrationNo"
        render={({ field: { onChange, ...rest } }) => (
          <Input
            {...rest}
            disabled={isSiretPendingAssignmentValue}
            onChange={(value) => {
              onChange(value)
              // Non-French compliance mode requires at least one of vat/registration number.
              // Revalidate both to keep error state in sync when either field changes or is cleared.
              if ((!isFrenchComplianceMode && vatNoError) || !value.target.value) {
                trigger(['registrationNo', 'vatNo'])
              }
            }}
            placeholder={registrationNoPlaceholder}
            prefix={isSiretPendingAssignmentValue && <Icon icon="info" />}
            size="sm"
          />
        )}
      />

      {shouldShowSiretPendingCheckbox && (
        <FormItem
          name="isSiretPendingAssignment"
          render={({ field: { value, ...field } }) => (
            <SiretPendingAssignmentCheckbox {...field} checked={value} registrationNoValue={registrationNoValue} />
          )}
        />
      )}
    </>
  )
}
